import { cn } from '@app/utils/cn';
import { IconComponent } from '@typings';
import React from 'react';

interface Props {
  onChange: (value: string | number) => void;
  selectedValue: string | number;
  groupName: string;
  options: IconOption[];
  className?: string;
  dataTestId?: string;
}

export interface IconOption {
  icon: IconComponent;
  value: string | number;
  name?: string;
}

export const IconRadioGroup = ({ onChange, selectedValue, options, groupName, className, dataTestId }: Props) => {
  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    },
    [onChange],
  );

  return (
    <fieldset>
      <legend className="sr-only">{groupName}</legend>
      <div role="radiogroup" className={cn('flex gap-1', className)} data-testid={dataTestId}>
        {options.map(option => {
          const checked = selectedValue === option.value;
          const Icon = option.icon;

          return (
            <label key={option.value} className="group">
              <input
                type="radio"
                name={groupName}
                value={option.value}
                checked={checked}
                aria-checked={checked}
                onChange={handleChange}
                className={cn('sr-only')}
              />
              <div
                className={cn(
                  'flex h-6 w-6 cursor-pointer items-center justify-center rounded-small border border-neutral-80 hover:border-neutral-60 group-focus-within:focus-outline',
                  {
                    'bg-neutral-90': checked,
                  },
                )}
              >
                <Icon className="size-5" />
              </div>
              <span className="sr-only">{option.name ?? option.value}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
};
